import { getLeagueDivisions, type League, type LeagueDivision, type LeagueTeamStanding, type Season } from './season-results';
import { canonicalTeamId } from './team-aliases';
import type { BroadcastTiming, PlayoffRound, SeasonGame } from '../types/scheduling-types';

/**
 * NFL-style season scheduling.
 *
 * Structure expected of every league in the season:
 *   league -> 2 conferences -> 4 divisions each -> 4 teams each
 * which is 16 teams per conference and 32 teams per league.
 *
 * Schedule shape (intra-conference single round robin):
 *   - Each team plays every other team in its OWN conference exactly once.
 *   - That is 15 opponents -> 15 weekly slates ("days").
 *   - Each slate is a perfect pairing of all 32 teams: 8 games per conference,
 *     16 games per slate, and every team plays exactly once that day.
 *   - 15 weeks * 16 games = 240 games total.
 *
 * Divisions are required for the structure check but do not influence who plays
 * whom under intra-conference round robin — matchups are conference-wide.
 */

export const NFL_CONFERENCES_PER_LEAGUE = 2;
export const NFL_DIVISIONS_PER_CONFERENCE = 4;
export const NFL_TEAMS_PER_DIVISION = 4;
export const NFL_TEAMS_PER_CONFERENCE = NFL_DIVISIONS_PER_CONFERENCE * NFL_TEAMS_PER_DIVISION; // 16
export const NFL_GAMES_PER_SLATE = NFL_TEAMS_PER_CONFERENCE; // 32 teams / 2 = 16
export const NFL_WEEKS_PER_SEASON = NFL_TEAMS_PER_CONFERENCE - 1; // 15 (single round robin)

/** Default first kickoff of every slate: UTC hour 24 = midnight UTC = 7:00 PM Central. */
export const SLATE_FIRST_KICKOFF_UTC_HOUR = 24;
/** By default a new game kicks off every 10 minutes. */
export const SLATE_KICKOFF_INTERVAL_MINUTES = 10;

/** Per-game broadcast pacing (milliseconds). */
export type BroadcastPacing = {
  introMs: number;
  playMs: number;
  betweenPlayMs: number;
  finalHoldMs: number;
};

/** Default per-game broadcast pacing. */
export const DEFAULT_BROADCAST_PACING: BroadcastPacing = {
  introMs: 10000,
  playMs: 8000,
  betweenPlayMs: 2000,
  finalHoldMs: 10000,
};

/**
 * Kickoff-timing knobs for a generated schedule. Every field is optional and
 * defaults to the values production has always used, so a plain request keeps
 * the existing rhythm.
 */
export interface SlateTimingOptions {
  /**
   * UTC hour of each slate's first kickoff (values past 23 roll into the next
   * UTC day, e.g. the default 24 = 7:00 PM Central).
   */
  firstKickoffUtcHour?: number;
  /** Minutes between consecutive kickoffs in a slate. Defaults to 10. */
  kickoffIntervalMinutes?: number;
  /** Number of games sharing each kickoff time. Defaults to 1. */
  gamesPerKickoffSlot?: number;
  /**
   * Maximum games on one calendar day; a larger slate spills onto the following
   * day(s). Defaults to the whole slate on one day. The spill must fit inside
   * `slateIntervalDays` so a slate never overlaps the next one.
   */
  gamesPerDay?: number;
  /**
   * Minutes added to every kickoff. Used to stagger leagues that share a season
   * so their ladders do not start at the identical instant. Defaults to 0.
   */
  kickoffOffsetMinutes?: number;
  /** Overrides for the per-game broadcast pacing (milliseconds). */
  broadcast?: Partial<BroadcastPacing>;
}

type ResolvedSlateTiming = {
  firstKickoffUtcHour: number;
  kickoffIntervalMinutes: number;
  gamesPerKickoffSlot: number;
  gamesPerDay: number;
  kickoffOffsetMinutes: number;
  broadcast: BroadcastPacing;
};

/** Fill defaults and validate a {@link SlateTimingOptions}. Throws on nonsense values. */
export function resolveSlateTiming(timing: SlateTimingOptions = {}): ResolvedSlateTiming {
  const resolved: ResolvedSlateTiming = {
    firstKickoffUtcHour: timing.firstKickoffUtcHour ?? SLATE_FIRST_KICKOFF_UTC_HOUR,
    kickoffIntervalMinutes: timing.kickoffIntervalMinutes ?? SLATE_KICKOFF_INTERVAL_MINUTES,
    gamesPerKickoffSlot: timing.gamesPerKickoffSlot ?? 1,
    gamesPerDay: timing.gamesPerDay ?? Number.POSITIVE_INFINITY,
    kickoffOffsetMinutes: timing.kickoffOffsetMinutes ?? 0,
    broadcast: { ...DEFAULT_BROADCAST_PACING, ...timing.broadcast },
  };

  const issues: string[] = [];
  if (!Number.isFinite(resolved.firstKickoffUtcHour) || resolved.firstKickoffUtcHour < 0 || resolved.firstKickoffUtcHour > 47) {
    issues.push(`firstKickoffUtcHour must be between 0 and 47, got ${resolved.firstKickoffUtcHour}`);
  }
  if (!Number.isFinite(resolved.kickoffIntervalMinutes) || resolved.kickoffIntervalMinutes < 0) {
    issues.push(`kickoffIntervalMinutes must be 0 or more, got ${resolved.kickoffIntervalMinutes}`);
  }
  if (!Number.isInteger(resolved.gamesPerKickoffSlot) || resolved.gamesPerKickoffSlot < 1) {
    issues.push(`gamesPerKickoffSlot must be a positive integer, got ${resolved.gamesPerKickoffSlot}`);
  }
  if (timing.gamesPerDay !== undefined && (!Number.isInteger(timing.gamesPerDay) || timing.gamesPerDay < 1)) {
    issues.push(`gamesPerDay must be a positive integer, got ${timing.gamesPerDay}`);
  }
  if (!Number.isFinite(resolved.kickoffOffsetMinutes) || resolved.kickoffOffsetMinutes < 0) {
    issues.push(`kickoffOffsetMinutes must be 0 or more, got ${resolved.kickoffOffsetMinutes}`);
  }
  for (const [key, value] of Object.entries(resolved.broadcast)) {
    if (!Number.isFinite(value) || value < 0) {
      issues.push(`broadcast.${key} must be 0 or more, got ${value}`);
    }
  }
  if (issues.length > 0) {
    throw new Error(`Invalid slate timing: ${issues.join('; ')}.`);
  }
  return resolved;
}

/**
 * Coerce an untrusted request-body value into a {@link SlateTimingOptions}:
 * finite numbers are kept, everything else is dropped. Returns undefined when
 * nothing usable was supplied. Range validation happens later, in
 * {@link resolveSlateTiming}, so bad values still fail loudly.
 */
export function parseSlateTimingInput(value: unknown): SlateTimingOptions | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const obj = value as Record<string, unknown>;
  const num = (raw: unknown): number | undefined => (typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined);

  const broadcastRaw = obj.broadcast && typeof obj.broadcast === 'object'
    ? obj.broadcast as Record<string, unknown>
    : {};
  const broadcast: SlateTimingOptions['broadcast'] = {};
  for (const key of Object.keys(DEFAULT_BROADCAST_PACING) as Array<keyof BroadcastPacing>) {
    const parsed = num(broadcastRaw[key]);
    if (parsed !== undefined) {
      broadcast[key] = parsed;
    }
  }

  const timing: SlateTimingOptions = {
    ...(num(obj.firstKickoffUtcHour) !== undefined ? { firstKickoffUtcHour: num(obj.firstKickoffUtcHour) } : {}),
    ...(num(obj.kickoffIntervalMinutes) !== undefined ? { kickoffIntervalMinutes: num(obj.kickoffIntervalMinutes) } : {}),
    ...(num(obj.gamesPerKickoffSlot) !== undefined ? { gamesPerKickoffSlot: num(obj.gamesPerKickoffSlot) } : {}),
    ...(num(obj.gamesPerDay) !== undefined ? { gamesPerDay: num(obj.gamesPerDay) } : {}),
    ...(num(obj.kickoffOffsetMinutes) !== undefined ? { kickoffOffsetMinutes: num(obj.kickoffOffsetMinutes) } : {}),
    ...(Object.keys(broadcast).length > 0 ? { broadcast } : {}),
  };
  return Object.keys(timing).length > 0 ? timing : undefined;
}

/**
 * Return a human-readable list of every way `season` fails the NFL-style
 * structure (2 conferences, 4 divisions each, 4 teams each, for every league).
 * An empty array means the season is valid.
 */
export function getNflSeasonStructureIssues(season: Season): string[] {
  const issues: string[] = [];

  if (!season.leagues || season.leagues.length === 0) {
    issues.push('season has no leagues');
    return issues;
  }

  for (const league of season.leagues) {
    const leagueLabel = `league "${league.id}"`;
    const conferences = league.conferences ?? [];

    if (conferences.length !== NFL_CONFERENCES_PER_LEAGUE) {
      issues.push(`${leagueLabel} has ${conferences.length} conference(s), expected ${NFL_CONFERENCES_PER_LEAGUE}`);
    }

    for (const conference of conferences) {
      const conferenceLabel = `${leagueLabel} conference "${conference.id}"`;
      const divisions = conference.divisions ?? [];

      if (divisions.length !== NFL_DIVISIONS_PER_CONFERENCE) {
        issues.push(`${conferenceLabel} has ${divisions.length} division(s), expected ${NFL_DIVISIONS_PER_CONFERENCE}`);
      }

      for (const division of divisions) {
        const teamCount = division.teamIds?.length ?? 0;
        if (teamCount !== NFL_TEAMS_PER_DIVISION) {
          issues.push(`${conferenceLabel} division "${division.id}" has ${teamCount} team(s), expected ${NFL_TEAMS_PER_DIVISION}`);
        }
      }
    }
  }

  return issues;
}

/** True when every league in the season matches the NFL-style structure. */
export function isNflStyleSeason(season: Season): boolean {
  return getNflSeasonStructureIssues(season).length === 0;
}

/** Throw with a descriptive message unless the season is NFL-style. */
export function assertNflStyleSeason(season: Season): void {
  const issues = getNflSeasonStructureIssues(season);
  if (issues.length > 0) {
    throw new Error(`Season "${season.id}" is not NFL-style:\n- ${issues.join('\n- ')}`);
  }
}

export interface BuildNflScheduleOptions {
  /** Which league to schedule. Defaults to the sole league when there is only one. */
  leagueId?: string;
  /** UTC calendar date of week 1's slate. Defaults to "now". */
  startDate?: Date;
  /** Days between weekly slates. Defaults to 7 (weekly). Set to 1 for consecutive days. */
  slateIntervalDays?: number;
  /** Optional simulator base URL used to populate watch/stats links. Omitted when absent. */
  baseUrl?: string;
  /** Value stamped onto every game's `updatedAt`. Defaults to "now"; pass a fixed value for deterministic output. */
  generatedAt?: Date;
  /** Kickoff-timing knobs (first kickoff hour, spacing, games per day, stagger). */
  timing?: SlateTimingOptions;
}

/**
 * Build the full intra-conference round-robin schedule for one league in the
 * season and return every game as a `SeasonGame`, ordered by week then kickoff.
 *
 * Throws if the season is not NFL-style, if the requested league is ambiguous
 * or missing, or if the season id is not a parseable `S<number>`.
 */
export function buildNflStyleSchedule(season: Season, options: BuildNflScheduleOptions = {}): SeasonGame[] {
  assertNflStyleSeason(season);

  const league = resolveLeague(season, options.leagueId);
  const [conferenceA, conferenceB] = league.conferences;
  const roundsA = roundRobinRounds(conferenceTeamIds(conferenceA));
  const roundsB = roundRobinRounds(conferenceTeamIds(conferenceB));

  // Each weekly slate interleaves one round from each conference so both are
  // spread across the evening: 8 + 8 = 16 games, every team playing once.
  const slates = roundsA.slice(0, NFL_WEEKS_PER_SEASON).map((roundA, weekIndex) => {
    const roundB = roundsB[weekIndex];
    const slate: Array<[string, string]> = [];
    for (let i = 0; i < roundA.length; i += 1) {
      slate.push(roundA[i]);
      slate.push(roundB[i]);
    }
    return slate;
  });

  return buildGamesFromSlates(slates, {
    seasonNumber: parseSeasonNumber(season.id),
    startDate: options.startDate ?? new Date(),
    slateIntervalDays: options.slateIntervalDays ?? 1,
    updatedAt: (options.generatedAt ?? new Date()).toISOString(),
    baseUrl: options.baseUrl,
    timing: resolveSlateTiming(options.timing),
  });
}

export interface BuildRoundRobinScheduleOptions {
  /** Games each team plays. Defaults to `teamIds.length - 1` (a full round robin). */
  gamesPerTeam?: number;
  /** Season id for gameIds and `SeasonGame.seasonId`. Accepts `"S0"` or `0`. Defaults to 0. */
  seasonId?: string | number;
  /** UTC calendar date of week 1's slate. Defaults to "now". */
  startDate?: Date;
  /** Days between weekly slates. Defaults to 1 (consecutive days). */
  slateIntervalDays?: number;
  /** Optional simulator base URL used to populate watch/stats links. Omitted when absent. */
  baseUrl?: string;
  /** Value stamped onto every game's `updatedAt`. Defaults to "now"; pass a fixed value for deterministic output. */
  generatedAt?: Date;
  /** Kickoff-timing knobs (first kickoff hour, spacing, games per day, stagger). */
  timing?: SlateTimingOptions;
}

/**
 * Flat round-robin builder. Given a flat list of teams (no divisions), build a
 * round robin where every team plays `gamesPerTeam` distinct opponents — one
 * game per slate, no rematches. Returns every game as a `SeasonGame`, ordered
 * by week then kickoff.
 *
 * Requires an even number of unique teams; `gamesPerTeam` defaults to a full
 * round robin (`teamIds.length - 1`) and cannot exceed it.
 */
export function buildRoundRobinSchedule(teamIds: string[], options: BuildRoundRobinScheduleOptions = {}): SeasonGame[] {
  const gamesPerTeam = options.gamesPerTeam ?? teamIds.length - 1;

  if (new Set(teamIds).size !== teamIds.length) {
    throw new Error('teamIds must be unique.');
  }
  const maxGames = teamIds.length - 1;
  if (gamesPerTeam < 1 || gamesPerTeam > maxGames) {
    throw new Error(`gamesPerTeam must be between 1 and ${maxGames} for ${teamIds.length} teams, got ${gamesPerTeam}.`);
  }

  // A full round robin is teamIds.length - 1 repeat-free rounds; taking the
  // first `gamesPerTeam` gives each team that many distinct opponents.
  const slates = roundRobinRounds(teamIds).slice(0, gamesPerTeam);

  return buildGamesFromSlates(slates, {
    seasonNumber: normalizeSeasonNumber(options.seasonId),
    startDate: options.startDate ?? new Date(),
    slateIntervalDays: options.slateIntervalDays ?? 1,
    updatedAt: (options.generatedAt ?? new Date()).toISOString(),
    baseUrl: options.baseUrl,
    timing: resolveSlateTiming(options.timing),
  });
}

/** Number of divisions a league must have to use the division-aware builder. */
export const DIVISION_SCHEDULE_DIVISIONS_REQUIRED = 4;

/**
 * The three ways to split four divisions into two cross-division pairs. Phase B
 * plays each pairing twice so every team draws opponents from all three other
 * divisions.
 */
const DIVISION_PAIRINGS: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  [[0, 1], [2, 3]],
  [[0, 2], [1, 3]],
  [[0, 3], [1, 2]],
];

/** Offsets used to slide one division's teams against another's in phase B. */
const PHASE_B_OFFSETS = [1, 2] as const;

export interface BuildDivisionScheduleOptions {
  /** Season id for gameIds and `SeasonGame.seasonId`. Accepts `"S0"` or `0`. Defaults to 0. */
  seasonId?: string | number;
  /** UTC calendar date of week 1's slate. Defaults to "now". */
  startDate?: Date;
  /** Days between weekly slates. Defaults to 1 (consecutive days). */
  slateIntervalDays?: number;
  /** Consecutive matchup rounds grouped onto each slate/day. Defaults to 1. */
  roundsPerSlate?: number;
  /** Optional simulator base URL used to populate watch/stats links. Omitted when absent. */
  baseUrl?: string;
  /** Value stamped onto every game's `updatedAt`. Defaults to "now"; pass a fixed value for deterministic output. */
  generatedAt?: Date;
  /** Kickoff-timing knobs (first kickoff hour, spacing, games per day, stagger). */
  timing?: SlateTimingOptions;
  /** Orient the complete schedule graph so every team has an even home/away split. */
  balanceHomeAway?: boolean;
  /** Optional per-slate kickoff slot for every game, used by fixed curated schedules. */
  kickoffSlotAssignments?: readonly (readonly number[])[];
}

/**
 * Describe every reason `league` cannot be scheduled by the division-aware
 * builder. The builder requires exactly {@link DIVISION_SCHEDULE_DIVISIONS_REQUIRED}
 * divisions (across the league's conferences) of equal size, with globally
 * unique team ids. An empty array means the league is eligible.
 */
export function getDivisionScheduleIssues(league: League): string[] {
  const issues: string[] = [];
  const divisions = getLeagueDivisions(league);

  if (divisions.length !== DIVISION_SCHEDULE_DIVISIONS_REQUIRED) {
    issues.push(`league "${league.id}" has ${divisions.length} division(s), expected ${DIVISION_SCHEDULE_DIVISIONS_REQUIRED}`);
    return issues;
  }

  const size = divisions[0].teamIds?.length ?? 0;
  if (size < 3) {
    issues.push(`league "${league.id}" divisions must hold at least 3 teams, got ${size}`);
  }
  for (const division of divisions) {
    const teamCount = division.teamIds?.length ?? 0;
    if (teamCount !== size) {
      issues.push(`league "${league.id}" division "${division.id}" has ${teamCount} team(s), expected ${size}`);
    }
  }

  const allTeamIds = divisions.flatMap((division) => division.teamIds ?? []);
  if (new Set(allTeamIds).size !== allTeamIds.length) {
    issues.push(`league "${league.id}" has duplicate team ids across its divisions`);
  }

  return issues;
}

/** True when `league` can be scheduled by {@link buildDivisionRoundRobinSchedule}. */
export function isDivisionScheduleLeague(league: League): boolean {
  return getDivisionScheduleIssues(league).length === 0;
}

/**
 * Division-aware builder for a league shaped as one conference of exactly four
 * equal divisions. Every team plays its entire division and cross-division
 * opponents, with every team appearing once in each generated round.
 *
 * Shape (m = teams per division, m odd):
 *   - Phase A (m weeks): each division runs a full internal round robin. Because
 *     m is odd one team per division sits out each week; the four idle teams are
 *     paired into two cross-division games so all 4·m teams still play. After
 *     phase A every team has met all m-1 division rivals plus one cross opponent.
 *   - Phase B (6 weeks): pure cross-division play. The four divisions are split
 *     into the three possible pairings; each pairing is played twice at distinct
 *     offsets, giving every team two fresh opponents from each of the other three
 *     divisions (three against its phase-A bye rival's division).
 *
 * Total: m+6 games per team across m+6 weeks (11 for the DFL's 4×5), 2·m games
 * per week. Throws (via {@link getDivisionScheduleIssues}) if the league shape is
 * ineligible.
 */
export function buildDivisionRoundRobinSchedule(
  league: League,
  options: BuildDivisionScheduleOptions = {},
): SeasonGame[] {
  const issues = getDivisionScheduleIssues(league);
  if (issues.length > 0) {
    throw new Error(`League "${league.id}" cannot use the division-aware schedule:\n- ${issues.join('\n- ')}`);
  }

  const roundsPerSlate = options.roundsPerSlate ?? 1;
  if (!Number.isInteger(roundsPerSlate) || roundsPerSlate < 1) {
    throw new Error(`roundsPerSlate must be a positive integer, got ${roundsPerSlate}.`);
  }
  const rounds = buildDivisionAwareRounds(getLeagueDivisions(league));
  const slates = groupRoundsIntoSlates(rounds, roundsPerSlate);

  return buildGamesFromSlates(slates, {
    seasonNumber: normalizeSeasonNumber(options.seasonId),
    startDate: options.startDate ?? new Date(),
    slateIntervalDays: options.slateIntervalDays ?? 1,
    updatedAt: (options.generatedAt ?? new Date()).toISOString(),
    baseUrl: options.baseUrl,
    timing: resolveSlateTiming(options.timing),
    balanceHomeAway: options.balanceHomeAway === true,
    kickoffSlotAssignments: options.kickoffSlotAssignments,
  });
}

/**
 * Build the ordered slates for {@link buildDivisionRoundRobinSchedule}. The
 * caller guarantees four divisions of equal size with unique team ids.
 */
function buildDivisionAwareRounds(divisions: LeagueDivision[]): Array<Array<[string, string]>> {
  const teams = divisions.map((division) => division.teamIds);
  const size = teams[0].length;

  // Even divisions use a complete league round robin, then one balanced
  // divisional rematch round. For 4x6 this is 24 rounds, which groups cleanly
  // into eight three-game-per-team days.
  if (size % 2 === 0) {
    const rounds = roundRobinRounds(teams.flat());
    const divisionalRematches: Array<[string, string]> = [];
    for (const division of teams) {
      divisionalRematches.push(...earliestPerfectMatching(division, rounds));
    }
    return [...rounds, divisionalRematches];
  }

  const slates: Array<Array<[string, string]>> = [];

  // Phase A: each division's internal round robin, with the four weekly byes
  // (the same positional index in every division) paired into two cross games.
  for (const round of oddRoundRobinIndexRounds(size)) {
    const slate: Array<[string, string]> = [];
    for (const division of teams) {
      for (const [i, j] of round.pairs) {
        slate.push([division[i], division[j]]);
      }
    }
    slate.push([teams[0][round.bye], teams[1][round.bye]]);
    slate.push([teams[2][round.bye], teams[3][round.bye]]);
    slates.push(slate);
  }

  // Phase B: each division pairing played twice at distinct, non-zero offsets.
  // Phase A's bye games are the offset-0 matching of pairing {0-1, 2-3}, so the
  // non-zero offsets here never reproduce them.
  for (const pairing of DIVISION_PAIRINGS) {
    for (const offset of PHASE_B_OFFSETS) {
      const slate: Array<[string, string]> = [];
      for (const [x, y] of pairing) {
        for (let i = 0; i < size; i += 1) {
          slate.push([teams[x][i], teams[y][(i + offset) % size]]);
        }
      }
      slates.push(slate);
    }
  }

  return slates;
}

/**
 * Choose a division-wide perfect matching whose original round-robin meetings
 * happened as early as possible. That prevents the added rematch round from
 * duplicating one of the other rounds grouped onto the final game day.
 */
function earliestPerfectMatching(
  division: string[],
  rounds: Array<Array<[string, string]>>,
): Array<[string, string]> {
  const roundByPair = new Map<string, number>();
  const key = (a: string, b: string) => [a, b].sort().join('|');
  rounds.forEach((round, roundIndex) => {
    for (const [a, b] of round) roundByPair.set(key(a, b), roundIndex);
  });

  let best: Array<[string, string]> | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  const search = (remaining: string[], pairs: Array<[string, string]>) => {
    if (remaining.length === 0) {
      const score = Math.max(...pairs.map(([a, b]) => roundByPair.get(key(a, b)) ?? rounds.length));
      if (score < bestScore) {
        bestScore = score;
        best = pairs.map(([a, b]) => [b, a]);
      }
      return;
    }
    const [first, ...rest] = remaining;
    for (let index = 0; index < rest.length; index += 1) {
      search(
        rest.filter((_, candidateIndex) => candidateIndex !== index),
        [...pairs, [first, rest[index]]],
      );
    }
  };
  search(division, []);
  return best ?? [];
}

/** Combine consecutive matchup rounds into one calendar slate. */
function groupRoundsIntoSlates(
  rounds: Array<Array<[string, string]>>,
  roundsPerSlate: number,
): Array<Array<[string, string]>> {
  const slates: Array<Array<[string, string]>> = [];
  for (let index = 0; index < rounds.length; index += roundsPerSlate) {
    slates.push(rounds.slice(index, index + roundsPerSlate).flat());
  }
  return slates;
}

/**
 * Circle-method round robin for an odd number of teams. Returns `m` rounds, each
 * pairing every team but one (the `bye`) — index-based so all divisions share the
 * same per-round bye position. Implemented by adding a dummy team to make the
 * count even; whoever draws the dummy takes the bye.
 */
function oddRoundRobinIndexRounds(m: number): Array<{ pairs: Array<[number, number]>; bye: number }> {
  const dummy = m;
  const n = m + 1; // even
  const fixed = 0;
  // Rotating ring is [1, 2, ..., m-1, dummy]; the fixed team 0 stays put.
  let rotating = [...Array.from({ length: m - 1 }, (_, i) => i + 1), dummy];

  const rounds: Array<{ pairs: Array<[number, number]>; bye: number }> = [];
  for (let round = 0; round < n - 1; round += 1) {
    const lineup = [fixed, ...rotating];
    const pairs: Array<[number, number]> = [];
    let bye = -1;
    for (let i = 0; i < n / 2; i += 1) {
      const a = lineup[i];
      const b = lineup[n - 1 - i];
      if (a === dummy) {
        bye = b;
      } else if (b === dummy) {
        bye = a;
      } else {
        pairs.push([a, b]);
      }
    }
    rounds.push({ pairs, bye });
    rotating = [rotating[rotating.length - 1], ...rotating.slice(0, rotating.length - 1)];
  }

  return rounds;
}

interface SlateBuildContext {
  seasonNumber: number;
  startDate: Date;
  slateIntervalDays: number;
  updatedAt: string;
  baseUrl?: string;
  timing: ResolvedSlateTiming;
  balanceHomeAway?: boolean;
  kickoffSlotAssignments?: readonly (readonly number[])[];
}

/**
 * Deterministically orient an undirected schedule through Euler circuits. An
 * even-degree schedule (Season 2 has degree 24) becomes exactly half home and
 * half away for every team. Odd-degree vertices receive one temporary dummy
 * edge, which keeps their final split within one game.
 */
export function orientSlatesForBalancedHomeAway(
  slates: Array<Array<[string, string]>>,
): Array<Array<[string, string]>> {
  type Edge = { first: string; second: string; slate: number; game: number; real: boolean };
  const edges: Edge[] = [];
  const degree = new Map<string, number>();
  slates.forEach((slate, slateIndex) => slate.forEach(([first, second], gameIndex) => {
    edges.push({ first, second, slate: slateIndex, game: gameIndex, real: true });
    degree.set(first, (degree.get(first) ?? 0) + 1);
    degree.set(second, (degree.get(second) ?? 0) + 1);
  }));

  const dummy = '__DFL_HOME_AWAY_DUMMY__';
  if (degree.has(dummy)) {
    throw new Error(`Team id ${dummy} is reserved by the balanced schedule builder.`);
  }
  for (const [teamId, teamDegree] of degree) {
    if (teamDegree % 2 !== 0) {
      edges.push({ first: teamId, second: dummy, slate: -1, game: -1, real: false });
    }
  }

  const adjacency = new Map<string, number[]>();
  const addAdjacent = (teamId: string, edgeIndex: number) => {
    const list = adjacency.get(teamId) ?? [];
    list.push(edgeIndex);
    adjacency.set(teamId, list);
  };
  edges.forEach((edge, edgeIndex) => {
    addAdjacent(edge.first, edgeIndex);
    addAdjacent(edge.second, edgeIndex);
  });

  const used = new Set<number>();
  const cursor = new Map<string, number>();
  const orientation = new Map<number, [string, string]>();
  const nextUnusedEdge = (teamId: string): number | null => {
    const list = adjacency.get(teamId) ?? [];
    let index = cursor.get(teamId) ?? 0;
    while (index < list.length && used.has(list[index])) index += 1;
    cursor.set(teamId, index + 1);
    return index < list.length ? list[index] : null;
  };

  for (const start of adjacency.keys()) {
    if ((adjacency.get(start) ?? []).every((edgeIndex) => used.has(edgeIndex))) continue;
    const stack = [start];
    while (stack.length > 0) {
      const from = stack[stack.length - 1];
      const edgeIndex = nextUnusedEdge(from);
      if (edgeIndex === null) {
        stack.pop();
        continue;
      }
      used.add(edgeIndex);
      const edge = edges[edgeIndex];
      const to = edge.first === from ? edge.second : edge.first;
      if (edge.real) orientation.set(edgeIndex, [from, to]);
      stack.push(to);
    }
  }

  const oriented = slates.map((slate) => slate.map((pairing) => [...pairing] as [string, string]));
  edges.forEach((edge, edgeIndex) => {
    if (edge.real) oriented[edge.slate][edge.game] = orientation.get(edgeIndex)!;
  });

  const homeAway = new Map<string, { away: number; home: number }>();
  for (const slate of oriented) {
    for (const [away, home] of slate) {
      const awayRecord = homeAway.get(away) ?? { away: 0, home: 0 };
      awayRecord.away += 1;
      homeAway.set(away, awayRecord);
      const homeRecord = homeAway.get(home) ?? { away: 0, home: 0 };
      homeRecord.home += 1;
      homeAway.set(home, homeRecord);
    }
  }
  for (const [teamId, record] of homeAway) {
    if (Math.abs(record.home - record.away) > 1) {
      throw new Error(`Could not balance home/away games for team ${teamId}.`);
    }
  }
  return oriented;
}

function validateKickoffSlotAssignments(
  slates: Array<Array<[string, string]>>,
  assignments: readonly (readonly number[])[],
  timing: ResolvedSlateTiming,
): void {
  if (assignments.length !== slates.length) {
    throw new Error(`Kickoff slot assignments need ${slates.length} slates, got ${assignments.length}.`);
  }
  if (Number.isFinite(timing.gamesPerDay)) {
    throw new Error('Curated kickoff slot assignments cannot be combined with gamesPerDay splitting.');
  }
  slates.forEach((slate, slateIndex) => {
    const row = assignments[slateIndex];
    if (row.length !== slate.length) {
      throw new Error(`Kickoff slot assignment ${slateIndex + 1} needs ${slate.length} games, got ${row.length}.`);
    }
    const slotCount = Math.ceil(slate.length / timing.gamesPerKickoffSlot);
    const gamesBySlot = Array(slotCount).fill(0) as number[];
    const slotsByTeam = new Map<string, Set<number>>();
    row.forEach((slot, gameIndex) => {
      if (!Number.isInteger(slot) || slot < 0 || slot >= slotCount) {
        throw new Error(`Invalid kickoff slot ${slot} for slate ${slateIndex + 1}, game ${gameIndex + 1}.`);
      }
      gamesBySlot[slot] += 1;
      for (const teamId of slate[gameIndex]) {
        const teamSlots = slotsByTeam.get(teamId) ?? new Set<number>();
        if (teamSlots.has(slot)) {
          throw new Error(`Team ${teamId} is double-booked in slot ${slot} on slate ${slateIndex + 1}.`);
        }
        teamSlots.add(slot);
        slotsByTeam.set(teamId, teamSlots);
      }
    });
    if (gamesBySlot.some((count) => count !== timing.gamesPerKickoffSlot)) {
      throw new Error(`Slate ${slateIndex + 1} does not place exactly ${timing.gamesPerKickoffSlot} games in every slot.`);
    }
  });
}

/**
 * Turn an ordered list of slates (each a list of `[teamA, teamB]` pairings) into
 * `SeasonGame`s. Slate N becomes week N+1; each slate's games kick off
 * in groups of `gamesPerKickoffSlot`, with groups `kickoffIntervalMinutes`
 * apart starting at `firstKickoffUtcHour`. Slates spill onto following days
 * when `gamesPerDay` caps a day.
 */
function buildGamesFromSlates(slates: Array<Array<[string, string]>>, ctx: SlateBuildContext): SeasonGame[] {
  const games: SeasonGame[] = [];
  const usedGameIds = new Set<string>();
  const { timing } = ctx;
  const scheduledSlates = ctx.balanceHomeAway ? orientSlatesForBalancedHomeAway(slates) : slates;
  if (ctx.kickoffSlotAssignments) {
    validateKickoffSlotAssignments(scheduledSlates, ctx.kickoffSlotAssignments, timing);
  }

  // A slate split across days must finish before the next slate starts.
  const largestSlate = scheduledSlates.reduce((max, slate) => Math.max(max, slate.length), 0);
  const daysPerSlate = Number.isFinite(timing.gamesPerDay) ? Math.ceil(largestSlate / timing.gamesPerDay) : 1;
  if (daysPerSlate > ctx.slateIntervalDays) {
    throw new Error(
      `gamesPerDay ${timing.gamesPerDay} spreads a ${largestSlate}-game slate across ${daysPerSlate} days, `
      + `but slates are only ${ctx.slateIntervalDays} day(s) apart. Raise slateIntervalDays or gamesPerDay.`,
    );
  }

  scheduledSlates.forEach((slate, weekIndex) => {
    const week = weekIndex + 1;

    slate.forEach((pairing, slotIndex) => {
      const [first, second] = pairing;
      const away = ctx.balanceHomeAway
        ? first
        : (weekIndex + slotIndex) % 2 === 0 ? first : second;
      const home = ctx.balanceHomeAway
        ? second
        : away === first ? second : first;

      const dayInSlate = Number.isFinite(timing.gamesPerDay) ? Math.floor(slotIndex / timing.gamesPerDay) : 0;
      const slotInDay = Number.isFinite(timing.gamesPerDay) ? slotIndex % timing.gamesPerDay : slotIndex;
      const kickoffSlot = ctx.kickoffSlotAssignments?.[weekIndex]?.[slotIndex]
        ?? Math.floor(slotInDay / timing.gamesPerKickoffSlot);
      const kickoff = kickoffAt(
        ctx.startDate,
        weekIndex * ctx.slateIntervalDays + dayInSlate,
        timing.kickoffOffsetMinutes + kickoffSlot * timing.kickoffIntervalMinutes,
        timing.firstKickoffUtcHour,
      );
      const baseGameId = `S${ctx.seasonNumber}-WK${week}-${away}-${home}`;
      const gameId = usedGameIds.has(baseGameId) ? `${baseGameId}-G${slotIndex + 1}` : baseGameId;
      usedGameIds.add(gameId);

      const game: SeasonGame = {
        gameId,
        seasonId: ctx.seasonNumber,
        week,
        index: slotIndex,
        date: kickoff,
        away,
        home,
        awayScore: 0,
        homeScore: 0,
        updatedAt: ctx.updatedAt,
        status: 'scheduled',
        broadcastTiming: createBroadcastTiming(kickoff, timing.broadcast),
        completedGameResult: null,
        matchupLiveSummary: null,
      };

      if (ctx.baseUrl) {
        const base = ctx.baseUrl.replace(/\/+$/, '');
        game.watchUrl = `${base}/?game=${encodeURIComponent(gameId)}`;
        game.statsUrl = `${base}/game/${encodeURIComponent(gameId)}/stats`;
      }

      games.push(game);
    });
  });

  return games;
}

function resolveLeague(season: Season, leagueId?: string): League {
  if (leagueId) {
    const match = season.leagues.find((league) => league.id === leagueId);
    if (!match) {
      throw new Error(`Season "${season.id}" has no league "${leagueId}".`);
    }
    return match;
  }

  if (season.leagues.length !== 1) {
    throw new Error(
      `Season "${season.id}" has ${season.leagues.length} leagues; pass options.leagueId to choose one.`,
    );
  }

  return season.leagues[0];
}

function parseSeasonNumber(seasonId: string): number {
  const digits = seasonId.replace(/[^0-9]/g, '');
  if (digits === '') {
    throw new Error(`Season id "${seasonId}" has no numeric component; expected e.g. "S5".`);
  }
  return Number(digits);
}

/** Accept either a numeric season (`5`) or a string id (`"S5"`); default 0. */
function normalizeSeasonNumber(seasonId: string | number | undefined): number {
  if (seasonId === undefined) {
    return 0;
  }
  return typeof seasonId === 'number' ? seasonId : parseSeasonNumber(seasonId);
}

function conferenceTeamIds(conference: League['conferences'][number]): string[] {
  const teamIds = conference.divisions.flatMap((division) => division.teamIds);
  if (teamIds.length !== NFL_TEAMS_PER_CONFERENCE) {
    throw new Error(
      `Conference "${conference.id}" has ${teamIds.length} teams, expected ${NFL_TEAMS_PER_CONFERENCE}.`,
    );
  }
  return teamIds;
}

/**
 * Circle-method single round robin. Returns `teams.length - 1` rounds, each a
 * full pairing of all teams (every team appears exactly once per round). The
 * team count must be even.
 */
export function roundRobinRounds(teamIds: string[]): Array<Array<[string, string]>> {
  const n = teamIds.length;
  if (n % 2 !== 0) {
    throw new Error(`Round robin requires an even number of teams, got ${n}.`);
  }

  const [fixed, ...rest] = teamIds;
  let rotating = rest;
  const rounds: Array<Array<[string, string]>> = [];

  for (let round = 0; round < n - 1; round += 1) {
    const lineup = [fixed, ...rotating];
    const games: Array<[string, string]> = [];
    for (let i = 0; i < n / 2; i += 1) {
      games.push([lineup[i], lineup[n - 1 - i]]);
    }
    rounds.push(games);
    // Rotate everyone except the fixed team one step.
    rotating = [rotating[rotating.length - 1], ...rotating.slice(0, rotating.length - 1)];
  }

  return rounds;
}

/** Kickoff `dayOffset` days after `startDate`, `minuteOffset` minutes into the slate's kickoff hour. */
function kickoffAt(startDate: Date, dayOffset: number, minuteOffset: number, firstKickoffUtcHour: number): Date {
  return new Date(
    Date.UTC(
      startDate.getUTCFullYear(),
      startDate.getUTCMonth(),
      startDate.getUTCDate() + dayOffset,
      firstKickoffUtcHour,
      minuteOffset,
      0,
      0,
    ),
  );
}

function createBroadcastTiming(date: Date, pacing: BroadcastPacing = DEFAULT_BROADCAST_PACING): BroadcastTiming {
  return {
    kickoffAt: date.toISOString(),
    ...pacing,
  };
}

// ---------------------------------------------------------------------------
// Playoffs
//
// Supported single-elimination brackets:
// - 8 teams: quarterfinals -> semifinals -> championship.
// - 12 teams: wild card -> quarterfinals -> semifinals -> championship, with
//   seeds 1-4 receiving first-round byes.
// Games are created up front with seed/winner placeholders, then progressively
// filled by `seedPlayoffBracket` and `resolvePlayoffBracket`.
// ---------------------------------------------------------------------------

/** Teams that qualify for the playoff bracket. */
export const PLAYOFF_BRACKET_SIZE = 8;
export const TWELVE_TEAM_PLAYOFF_BRACKET_SIZE = 12;
export type PlayoffBracketSize = typeof PLAYOFF_BRACKET_SIZE | typeof TWELVE_TEAM_PLAYOFF_BRACKET_SIZE;

/** Stable bracket keys, also used as each playoff game's gameId suffix. */
export const PLAYOFF_GAME_KEYS = ['QF1', 'QF2', 'QF3', 'QF4', 'SF1', 'SF2', 'FINAL'] as const;
export const TWELVE_TEAM_PLAYOFF_GAME_KEYS = [
  'WC1', 'WC2', 'WC3', 'WC4',
  'QF1', 'QF2', 'QF3', 'QF4',
  'SF1', 'SF2', 'FINAL',
] as const;
export type PlayoffGameKey =
  | (typeof PLAYOFF_GAME_KEYS)[number]
  | (typeof TWELVE_TEAM_PLAYOFF_GAME_KEYS)[number];

/**
 * The quarterfinal each seed plays in, as `[higherSeed, lowerSeed]` (1-based).
 * Mirrors the NFL bracket so the top two seeds can only meet in the final.
 */
const EIGHT_TEAM_SEED_POSITIONS: Partial<Record<PlayoffGameKey, readonly [number, number]>> = {
  QF1: [1, 8],
  QF2: [4, 5],
  QF3: [3, 6],
  QF4: [2, 7],
};

const TWELVE_TEAM_SEED_POSITIONS: Partial<Record<PlayoffGameKey, readonly [number, number?]>> = {
  WC1: [8, 9],
  WC2: [5, 12],
  WC3: [6, 11],
  WC4: [7, 10],
  QF1: [1],
  QF2: [4],
  QF3: [3],
  QF4: [2],
};

/**
 * Legacy eight-team seed-slot list. Use {@link playoffSeedSlots} when the field
 * size is dynamic.
 */
export const PLAYOFF_SEED_SLOTS = ['SEED1', 'SEED2', 'SEED3', 'SEED4', 'SEED5', 'SEED6', 'SEED7', 'SEED8'] as const;

export function playoffSeedSlots(bracketSize: PlayoffBracketSize): string[] {
  return Array.from({ length: bracketSize }, (_, index) => `SEED${index + 1}`);
}

/** The 1-based seed a `SEEDn` token stands for, or `null` if it is not a seed slot. */
function seedSlotNumber(token: string): number | null {
  const match = /^SEED(\d{1,2})$/.exec(token);
  if (!match) return null;
  const seed = Number(match[1]);
  return seed >= 1 && seed <= TWELVE_TEAM_PLAYOFF_BRACKET_SIZE ? seed : null;
}

/** Placeholder team token for "winner of <gameKey>", e.g. `WQF1`. */
function winnerToken(gameKey: PlayoffGameKey): string {
  return `W${gameKey}`;
}

/** The game key a placeholder token refers to, or `null` if it is a real team id. */
function tokenSourceKey(token: string): PlayoffGameKey | null {
  const match = /^W(WC[1-4]|QF[1-4]|SF[12])$/.exec(token);
  return match ? (match[1] as PlayoffGameKey) : null;
}

/** Infer which supported bracket format a stored bracket uses. */
export function playoffBracketSizeForGames(games: SeasonGame[]): PlayoffBracketSize {
  return games.some((game) => playoffGameKey(game).startsWith('WC'))
    ? TWELVE_TEAM_PLAYOFF_BRACKET_SIZE
    : PLAYOFF_BRACKET_SIZE;
}

/** True when a home/away value is still a placeholder (seed slot or winner-of), not a real team. */
function isPlaceholderTeam(token: string): boolean {
  return seedSlotNumber(token) !== null || tokenSourceKey(token) !== null;
}

/** True when a game is part of a playoff bracket. */
export function isPlayoffGame(game: SeasonGame): boolean {
  return Boolean(game.playoffRound);
}

/** Recover a playoff game's bracket key (e.g. `FINAL`) from its slot-based gameId. */
export function playoffGameKey(game: SeasonGame): string {
  const segments = game.gameId.split('-');
  return segments[segments.length - 1];
}

/**
 * Pick and seed the playoff field NFL-style: each division's leader (its
 * best-ranked team in `standings`) is a seed, ordered 1..N by record, then the
 * best remaining teams league-wide fill the field as wild cards. `standings`
 * must already be sorted best-first (as {@link LeagueStandingsPayload} provides).
 * Returns up to {@link PLAYOFF_BRACKET_SIZE} team ids in seed order.
 */
export function selectPlayoffSeeds(
  standings: LeagueTeamStanding[],
  divisions: LeagueDivision[],
  bracketSize: number = PLAYOFF_BRACKET_SIZE,
): string[] {
  const rank = new Map(standings.map((standing, index) => [standing.teamId, index]));
  const seeds: string[] = [];
  const chosen = new Set<string>();

  const divisionWinners = divisions
    .map((division) => standings.find((standing) => division.teamIds.includes(standing.teamId)))
    .filter((standing): standing is LeagueTeamStanding => Boolean(standing))
    .sort((a, b) => (rank.get(a.teamId) ?? 0) - (rank.get(b.teamId) ?? 0));

  for (const winner of divisionWinners) {
    if (seeds.length >= bracketSize) break;
    seeds.push(winner.teamId);
    chosen.add(winner.teamId);
  }
  for (const standing of standings) {
    if (seeds.length >= bracketSize) break;
    if (!chosen.has(standing.teamId)) {
      seeds.push(standing.teamId);
      chosen.add(standing.teamId);
    }
  }

  return seeds;
}

export interface BuildPlayoffBracketOptions {
  /** Season id for gameIds and `SeasonGame.seasonId`. Accepts `"S0"` or `0`. Defaults to 0. */
  seasonId?: string | number;
  /** League the bracket belongs to; stamped onto every game so it is not treated as exhibition. */
  leagueId?: string;
  /** Week number of the first playoff round. Defaults to 12. */
  startWeek?: number;
  /** UTC calendar date of the first playoff round; later rounds follow daily. Defaults to "now". */
  startDate?: Date;
  /** Optional simulator base URL used to populate watch/stats links. Omitted when absent. */
  baseUrl?: string;
  /** Value stamped onto every game's `updatedAt`. Defaults to "now"; pass a fixed value for deterministic output. */
  generatedAt?: Date;
  /** Supported postseason field size. Existing leagues default to eight. */
  bracketSize?: PlayoffBracketSize;
  /**
   * Kickoff-timing knobs (first kickoff hour, spacing, stagger, broadcast
   * pacing). `gamesPerDay` is ignored — each bracket round is its own day.
   */
  timing?: SlateTimingOptions;
}

/**
 * Build an 8- or 12-team playoff bracket from a seed-ordered team list (seed 1
 * first). The opening round carries real teams (higher seed hosts); later rounds
 * carry winner placeholders filled by {@link resolvePlayoffBracket}.
 */
export function buildPlayoffBracket(seeds: string[], options: BuildPlayoffBracketOptions = {}): SeasonGame[] {
  const bracketSize = options.bracketSize ?? PLAYOFF_BRACKET_SIZE;
  if (seeds.length < bracketSize) {
    throw new Error(`Playoff bracket needs ${bracketSize} seeds, got ${seeds.length}.`);
  }
  if (new Set(seeds.slice(0, bracketSize)).size !== bracketSize) {
    throw new Error('Playoff seeds must be unique.');
  }

  const seasonNumber = normalizeSeasonNumber(options.seasonId);
  const startWeek = options.startWeek ?? 12;
  const startDate = options.startDate ?? new Date();
  const updatedAt = (options.generatedAt ?? new Date()).toISOString();
  const timing = resolveSlateTiming(options.timing);
  const teamForSeed = (seed: number): string => seeds[seed - 1];

  // [gameKey, round, dayOffset, home token, away token]
  const specs: Array<[PlayoffGameKey, PlayoffRound, number, string, string]> = bracketSize === TWELVE_TEAM_PLAYOFF_BRACKET_SIZE
    ? [
      ['WC1', 'wildcard', 0, teamForSeed(8), teamForSeed(9)],
      ['WC2', 'wildcard', 0, teamForSeed(5), teamForSeed(12)],
      ['WC3', 'wildcard', 0, teamForSeed(6), teamForSeed(11)],
      ['WC4', 'wildcard', 0, teamForSeed(7), teamForSeed(10)],
      ['QF1', 'quarterfinal', 1, teamForSeed(1), winnerToken('WC1')],
      ['QF2', 'quarterfinal', 1, teamForSeed(4), winnerToken('WC2')],
      ['QF3', 'quarterfinal', 1, teamForSeed(3), winnerToken('WC3')],
      ['QF4', 'quarterfinal', 1, teamForSeed(2), winnerToken('WC4')],
      ['SF1', 'semifinal', 2, winnerToken('QF1'), winnerToken('QF2')],
      ['SF2', 'semifinal', 2, winnerToken('QF4'), winnerToken('QF3')],
      ['FINAL', 'championship', 3, winnerToken('SF1'), winnerToken('SF2')],
    ]
    : [
      ['QF1', 'quarterfinal', 0, teamForSeed(1), teamForSeed(8)],
      ['QF2', 'quarterfinal', 0, teamForSeed(4), teamForSeed(5)],
      ['QF3', 'quarterfinal', 0, teamForSeed(3), teamForSeed(6)],
      ['QF4', 'quarterfinal', 0, teamForSeed(2), teamForSeed(7)],
      ['SF1', 'semifinal', 1, winnerToken('QF1'), winnerToken('QF2')],
      ['SF2', 'semifinal', 1, winnerToken('QF4'), winnerToken('QF3')],
      ['FINAL', 'championship', 2, winnerToken('SF1'), winnerToken('SF2')],
    ];

  const slotByDay = new Map<number, number>();
  return specs.map(([key, round, dayOffset, home, away]) => {
    const slotIndex = slotByDay.get(dayOffset) ?? 0;
    slotByDay.set(dayOffset, slotIndex + 1);
    const kickoffSlot = Math.floor(slotIndex / timing.gamesPerKickoffSlot);

    const kickoff = kickoffAt(
      startDate,
      dayOffset,
      timing.kickoffOffsetMinutes + kickoffSlot * timing.kickoffIntervalMinutes,
      timing.firstKickoffUtcHour,
    );
    // The league segment keeps brackets from colliding when several leagues in
    // one season each carry their own playoffs (their gameIds share the season).
    const gameId = options.leagueId
      ? `S${seasonNumber}-${options.leagueId}-${key}`
      : `S${seasonNumber}-${key}`;
    const game: SeasonGame = {
      gameId,
      seasonId: seasonNumber,
      week: startWeek + dayOffset,
      index: slotIndex,
      date: kickoff,
      away,
      home,
      awayScore: 0,
      homeScore: 0,
      updatedAt,
      status: 'scheduled',
      playoffRound: round,
      broadcastTiming: createBroadcastTiming(kickoff, timing.broadcast),
      completedGameResult: null,
      matchupLiveSummary: null,
      ...(options.leagueId ? { leagueId: options.leagueId } : {}),
    };

    if (options.baseUrl) {
      const base = options.baseUrl.replace(/\/+$/, '');
      game.watchUrl = `${base}/?game=${encodeURIComponent(gameId)}`;
      game.statsUrl = `${base}/game/${encodeURIComponent(gameId)}/stats`;
    }

    return game;
  });
}

/**
 * Fill in any playoff games whose home/away are still placeholder seed tokens,
 * using the winners of the games that feed them. Home/away are assigned so the
 * better seed hosts. Returns the bracket with every now-determinable matchup
 * resolved; games still waiting on an unplayed feeder are returned unchanged.
 * Idempotent — re-running with the same results yields the same bracket.
 */
export function resolvePlayoffBracket(games: SeasonGame[]): SeasonGame[] {
  const byKey = new Map<string, SeasonGame>();
  for (const game of games) {
    byKey.set(playoffGameKey(game), game);
  }

  // Reconstruct each team's seed number from the bracket's fixed seed positions.
  // Skip slots still holding a placeholder — a blank bracket carries no seeds yet.
  const bracketSize = playoffBracketSizeForGames(games);
  const seedPositions = bracketSize === TWELVE_TEAM_PLAYOFF_BRACKET_SIZE
    ? TWELVE_TEAM_SEED_POSITIONS
    : EIGHT_TEAM_SEED_POSITIONS;
  const seedOf = new Map<string, number>();
  for (const [key, [higher, lower]] of Object.entries(seedPositions)) {
    const game = byKey.get(key);
    if (!game) continue;
    if (!isPlaceholderTeam(game.home)) seedOf.set(canonicalTeamId(game.home), higher);
    if (lower !== undefined && !isPlaceholderTeam(game.away)) {
      seedOf.set(canonicalTeamId(game.away), lower);
    }
  }

  const winnerOf = (key: string): string | null => {
    const result = byKey.get(key)?.completedGameResult;
    return result?.winner ? canonicalTeamId(result.winner) : null;
  };
  // A real team resolves to itself; a winner-of token to that game's winner (or
  // null if unplayed); an unseeded SEED slot is not yet determinable.
  const resolveToken = (token: string): string | null => {
    if (seedSlotNumber(token) !== null) return null;
    const source = tokenSourceKey(token);
    // Preserve an already-concrete matchup exactly as it was stored. Legacy
    // initials remain valid read aliases, while newly resolved winners are
    // written with their canonical token id by winnerOf(). This also keeps the
    // resolver idempotent if the alias registry is hydrated between calls.
    return source ? winnerOf(source) : token;
  };

  return games.map((game) => {
    const home = resolveToken(game.home);
    const away = resolveToken(game.away);
    if (home === null || away === null) return game; // a feeder game is unplayed
    if (home === game.home && away === game.away) return game; // already concrete

    // Better seed (smaller number) hosts; unseeded sorts last.
    const homeSeed = seedOf.get(home) ?? Number.POSITIVE_INFINITY;
    const awaySeed = seedOf.get(away) ?? Number.POSITIVE_INFINITY;
    const [host, visitor] = homeSeed <= awaySeed ? [home, away] : [away, home];
    return { ...game, home: host, away: visitor };
  });
}

/**
 * Replace `SEED1`..`SEED12` placeholder tokens in a blank bracket's opening games
 * with the real seeded teams (`seeds[0]` is seed 1). Home/away are left as-is —
 * the blank bracket already put the higher seed slot at home. Games without seed
 * tokens (later rounds and already-seeded games) are returned unchanged, so this
 * is idempotent. Requires a complete field for the bracket format.
 */
export function seedPlayoffBracket(games: SeasonGame[], seeds: string[]): SeasonGame[] {
  const bracketSize = playoffBracketSizeForGames(games);
  if (seeds.length < bracketSize) {
    throw new Error(`Seeding the playoff bracket needs ${bracketSize} seeds, got ${seeds.length}.`);
  }

  const substitute = (token: string): string => {
    const seed = seedSlotNumber(token);
    return seed === null ? token : seeds[seed - 1];
  };

  return games.map((game) => {
    const home = substitute(game.home);
    const away = substitute(game.away);
    if (home === game.home && away === game.away) return game;
    return { ...game, home, away };
  });
}

// ---------------------------------------------------------------------------
// League structures
//
// The three schedule shapes a league can be built with, plus helpers to
// validate a team count against a shape and to lay a flat team list out into
// the conference/division structure that shape's builder expects. Used by
// season creation (every production league is `nfl`) and by custom leagues
// (the admin picks the structure).
// ---------------------------------------------------------------------------

/**
 * How a league's regular season is built:
 * - `nfl`: 2 conferences × 4 divisions × 4 teams (exactly 32), intra-conference
 *   single round robin ({@link buildNflStyleSchedule}).
 * - `divisions`: 1 conference of 4 equal divisions (12-32 teams in multiples of
 *   four), division play + cross-division slates
 *   ({@link buildDivisionRoundRobinSchedule}).
 * - `round-robin`: a flat league with no meaningful divisions (any even team
 *   count), full round robin ({@link buildRoundRobinSchedule}).
 */
export type LeagueScheduleStructure = 'nfl' | 'divisions' | 'round-robin';

export const LEAGUE_SCHEDULE_STRUCTURES: readonly LeagueScheduleStructure[] = ['nfl', 'divisions', 'round-robin'];

export const NFL_TEAMS_PER_LEAGUE = NFL_CONFERENCES_PER_LEAGUE * NFL_TEAMS_PER_CONFERENCE; // 32

/** Bounds on how many teams an admin-created custom league may hold. */
export const CUSTOM_LEAGUE_MIN_TEAMS = 8;
export const CUSTOM_LEAGUE_MAX_TEAMS = 32;

/**
 * Describe every reason `teamCount` teams cannot be scheduled with `structure`.
 * An empty array means the count fits the shape.
 */
export function getStructureTeamCountIssues(structure: LeagueScheduleStructure, teamCount: number): string[] {
  switch (structure) {
    case 'nfl':
      return teamCount === NFL_TEAMS_PER_LEAGUE
        ? []
        : [`nfl structure needs exactly ${NFL_TEAMS_PER_LEAGUE} teams (2 conferences × 4 divisions × 4), got ${teamCount}`];
    case 'divisions': {
      const perDivision = teamCount / DIVISION_SCHEDULE_DIVISIONS_REQUIRED;
      return Number.isInteger(perDivision) && perDivision >= 3
        ? []
        : [`divisions structure needs 4 equal divisions with at least 3 teams each, got ${teamCount}`];
    }
    case 'round-robin':
      return teamCount >= 2 && teamCount % 2 === 0
        ? []
        : [`round-robin structure needs an even number of teams (2+), got ${teamCount}`];
  }
}

export interface BuildLeagueForStructureOptions {
  leagueId: string;
  leagueName?: string;
  teamIds: string[];
  structure: LeagueScheduleStructure;
  divisionNames?: string[];
  playoffTeamCount?: 8 | 12;
}

/**
 * Lay a flat team list out into the {@link League} shape `structure`'s builder
 * expects, slicing `teamIds` in order (shuffle first for random assignment).
 * Conference/division ids are derived from the league id so they stay unique
 * across a multi-league season. Throws if the team count does not fit the
 * structure or the ids are not unique.
 */
export function buildLeagueForStructure(options: BuildLeagueForStructureOptions): League {
  const { leagueId, teamIds, structure } = options;
  const issues = getStructureTeamCountIssues(structure, teamIds.length);
  if (new Set(teamIds).size !== teamIds.length) {
    issues.push('team ids must be unique');
  }
  if (issues.length > 0) {
    throw new Error(`Cannot build league "${leagueId}": ${issues.join('; ')}.`);
  }

  const name = options.leagueName ?? leagueId;
  const division = (id: string, divisionName: string, teams: string[]): LeagueDivision => ({
    id,
    name: divisionName,
    teamIds: teams,
  });

  if (structure === 'nfl') {
    const conferences = ['A', 'B'].map((conferenceKey, conferenceIndex) => ({
      id: `${leagueId}-${conferenceKey}`,
      name: `${name} Conference ${conferenceKey}`,
      divisions: Array.from({ length: NFL_DIVISIONS_PER_CONFERENCE }, (_, divisionIndex) => {
        const start = conferenceIndex * NFL_TEAMS_PER_CONFERENCE + divisionIndex * NFL_TEAMS_PER_DIVISION;
        return division(
          `${leagueId}-${conferenceKey}${divisionIndex + 1}`,
          `Division ${conferenceKey}${divisionIndex + 1}`,
          teamIds.slice(start, start + NFL_TEAMS_PER_DIVISION),
        );
      }),
    }));
    return {
      id: leagueId,
      name,
      ...(options.playoffTeamCount ? { playoffTeamCount: options.playoffTeamCount } : {}),
      conferences,
    };
  }

  if (structure === 'divisions') {
    const perDivision = teamIds.length / DIVISION_SCHEDULE_DIVISIONS_REQUIRED;
    const divisions = Array.from({ length: DIVISION_SCHEDULE_DIVISIONS_REQUIRED }, (_, index) =>
      division(
        `${leagueId}-D${index + 1}`,
        options.divisionNames?.[index] || `Division ${index + 1}`,
        teamIds.slice(index * perDivision, (index + 1) * perDivision),
      ));
    return {
      id: leagueId,
      name,
      ...(options.playoffTeamCount ? { playoffTeamCount: options.playoffTeamCount } : {}),
      conferences: [{ id: leagueId, name, divisions }],
    };
  }

  // round-robin: one division holding everyone, so registry consumers (the
  // team→league index, standings) still see the membership.
  return {
    id: leagueId,
    name,
    ...(options.playoffTeamCount ? { playoffTeamCount: options.playoffTeamCount } : {}),
    conferences: [{ id: leagueId, name, divisions: [division(`${leagueId}-ALL`, name, teamIds)] }],
  };
}

/**
 * Deterministic Fisher–Yates shuffle driven by a mulberry32 PRNG, so a league
 * draw can be reproduced from its recorded seed. Returns a new array.
 */
export function seededShuffle<T>(items: T[], seed: number): T[] {
  let state = seed >>> 0;
  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(next() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
